<template>
  <div ref="$mapContainer" class="em-map-base" :class="{ 'layer-visible': !!sortedStores }">
    <template v-if="!hasConsented && !didAskForConsent">
      <slot name="modal"></slot>
      <img class="blurred-images" src="/images/map-blurred.jpg" />
    </template>
    <template v-else>
      <GoogleMap
        v-if="hasConsented"
        ref="googleMapRef"
        class="google-map"
        :class="{ 'layer-visible': !!sortedStores }"
        :apiKey="googleMapsApiKeyPublic"
        :center="initialLocation"
        :zoom="initialZoom"
        :styles="mapStyles"
        :zoomControl="false"
        :fullscreenControl="false"
        :streetViewControl="false"
        :mapTypeControl="false"
        :backgroundColor="mapBackgroundColor">
        <MarkerCluster v-if="googleMapRef?.api && algorithm" :options="{ renderer, algorithm }">
          <template v-for="store in stores">
            <Marker
              v-if="store.content?.location?.latitude && store.content?.location?.longitude"
              :key="store.uuid"
              :options="{
                position: {
                  lat: store.content.location.latitude,
                  lng: store.content.location.longitude,
                },
                icon: {
                  url: store.content.is_showroom
                    ? `/icons/map-pin-showroom${selectedStore?.uuid === store.uuid ? '-selected' : ''}.svg`
                    : `/icons/map-pin-partner${selectedStore?.uuid === store.uuid ? '-selected' : ''}.svg`,
                  scaledSize: new googleMapRef.api.Size(40, 40),
                },
              }"
              @click="() => handlePinClick(store)" />
          </template>
        </MarkerCluster>
      </GoogleMap>
      <div
        v-else-if="fallbackImageDesktop && fallbackImageMobile"
        class="google-map"
        :class="{ 'layer-visible': !!sortedStores }">
        <SharedBaseImage
          :image="breakpointState.isTablet ? fallbackImageDesktop : fallbackImageMobile"
          class="fallback-image"
          :breakpointsWidthMap="{
            '0': 375,
            '375': 706,
            '768': 942,
            '1024': 1178,
            '1280': 1325,
            '1440': 1920,
          }" />
        <p class="consent-info-text text-small-regular">
          {{ translations.consentText }}
          <button class="consent-info-button" @click="requestConsentChange">
            {{ translations.consentButtonLabel }}</button
          >.
        </p>
      </div>
      <SharedEMMapResultsLayer
        :isVisible="!!sortedStores"
        :results="sortedStores ?? []"
        :locationCountryCode="currentLocationResult?.countryCode"
        :storeContactType="storeContactType"
        :buttonLink="buttonLink"
        :selectedStore="selectedStore"
        :translations="translations"
        :brand="brand"
        @storeSelected="handleStoreSelected">
        <template #load-more-button>
          <slot name="load-more-button"></slot>
        </template>
        <template #contact-form-button>
          <slot name="contact-form-button"></slot>
        </template>
      </SharedEMMapResultsLayer>
      <SharedEMMapSearchInput
        :brand="brand"
        class="search-input"
        :class="{ 'no-consent': !hasConsented }"
        :resultsOpen="!!sortedStores"
        :translations="translations"
        @focusChange="handleFocus"
        @locationChange="handleLocationChange" />
      <div class="mobile-autocomplete-bg" :class="{ 'is-visible': mobileInputVisible }"></div>
      <div v-if="hasConsented" class="zoom-controls">
        <SharedEMMapZoomButton icon="fi_plus" @click="() => changeZoom(1)" />
        <SharedEMMapZoomButton icon="fi_minus" @click="() => changeZoom(-1)" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import haversine from 'haversine-distance';
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { GoogleMap, Marker, MarkerCluster } from 'vue3-google-map';
import type { MarkerClustererOptions } from '@googlemaps/markerclusterer';
import { SharedBaseImage } from '@seb-platform/shared/components/base';
import { useBreakpoint, useConsent, useStoryblokClient } from '@seb-platform/shared/composables';
import type { LocationResult } from '@seb-platform/shared/types/google-maps';
import { OneTrustGroup } from '@seb-platform/shared/types/onetrust';
import type { AssetStoryblok, MultilinkStoryblok, StoreEntryStoryblok } from '@seb-platform/shared/types/storyblok';
import { Brand, SebEvent } from '@seb-platform/shared/types/utils';
import type { EMMapTranslations, SortedStore, Store } from './SharedEMMap.types';
import SharedEMMapResultsLayer from './SharedEMMapResultsLayer.vue';
import SharedEMMapSearchInput from './SharedEMMapSearchInput.vue';
import SharedEMMapZoomButton from './SharedEMMapZoomButton.vue';

const [MIN_ZOOM, MAX_ZOOM] = [2, 22];
const initialLocation = { lat: 51.3, lng: 10.5 };

defineProps<{
  brand: Brand;
  mapStyles: google.maps.MapTypeStyle[];
  mapBackgroundColor: string;
  fallbackImageMobile: AssetStoryblok;
  fallbackImageDesktop: AssetStoryblok;
  storeContactType: '' | 'button' | 'list' | 'both';
  buttonLink?: Exclude<MultilinkStoryblok, { linktype?: 'email' } | { linktype?: 'asset' }>;
  googleMapsApiKeyPublic: string;
  translations: EMMapTranslations;
  initialZoom: number;
}>();

const $mapContainer = useTemplateRef('$mapContainer');
const sortedStores = ref<SortedStore[] | null>(null);
const currentLocationResult = ref<LocationResult | null>(null);
const hasConsented = computed(() => consentState.groups.includes(OneTrustGroup.Tracking));
const didAskForConsent = ref(false);
const mobileInputVisible = ref(false);
const selectedStore = ref<Store | null>(null);
const algorithm = ref<MarkerClustererOptions['algorithm'] | null>(null);
const googleMapRef = ref<{
  mapRef: HTMLElement | undefined;
  ready: boolean;
  map: google.maps.Map | undefined;
  api: typeof google.maps | undefined;
  mapTilesLoaded: boolean;
} | null>(null);

const renderer: MarkerClustererOptions['renderer'] = {
  render: ({ count, position }) => {
    return new googleMapRef.value!.api!.Marker({
      label: {
        text: String(count),
        className: 'marker-cluster-number',
      },
      position,
      icon: {
        url: `/icons/map-pin-partner.svg`,
        scaledSize: new googleMapRef.value!.api!.Size(40, 40),
      },
      zIndex: Number(googleMapRef.value!.api!.Marker.MAX_ZINDEX) + count,
    });
  },
};

const { state: consentState } = useConsent();
const { getStories } = await useStoryblokClient();
const { state: breakpointState } = useBreakpoint();

const stores = await getStories<StoreEntryStoryblok>({
  starts_with: '_stores/',
  content_type: 'store_entry',
});

/**
 * Sorts stores by assigned countries and distance and adjusts the map view
 */
function handleLocationChange(locationResult: LocationResult | null) {
  if (locationResult && stores.value) {
    currentLocationResult.value = locationResult;
    const [assignedStores, storesOthers] = stores.value
      // Remove stores without location
      .filter(store => !!store.content.location?.latitude && !!store.content.location?.longitude)
      // Split stores into 2 categories (assigned and others)
      .reduce(
        ([assignedStores, otherStores]: Store[][], store) => {
          if (store.content.assigned_countries.includes(locationResult.countryCode ?? 'NOT_FOUND')) {
            // Store is assigned to the country
            return [[store, ...assignedStores], otherStores];
          } else {
            return [assignedStores, [store, ...otherStores]];
          }
        },
        [[], [], []]
      );

    sortedStores.value = [
      ...sortStoresByDistance(assignedStores, locationResult),
      ...sortStoresByDistance(storesOthers, locationResult),
    ];
    selectedStore.value = sortedStores.value[0]?.store ?? null;

    // Zoom to the bounds of the first 3 stores
    if (googleMapRef.value?.api && googleMapRef.value?.map) {
      const storesToPick = Math.max(3, assignedStores.length); // Make sure that all assigned stores are visible
      const locationsToShow = sortedStores.value
        .slice(0, storesToPick)
        .filter(s => s.store.content.location?.latitude && s.store.content.location?.longitude)
        .map(
          s =>
            new googleMapRef.value!.api!.LatLng(
              s.store.content.location!.latitude!,
              s.store.content.location!.longitude
            )
        );
      const latlngbounds = new google.maps.LatLngBounds();
      locationsToShow.forEach(location => latlngbounds.extend(location));

      /**
       * On desktop, the result container is above the map, so we need to delay the fitBounds call a little bit.
       * If we don't do this, a marker might not be visible when it is positioned below the result container.
       */
      setTimeout(() => {
        googleMapRef.value!.map!.fitBounds(latlngbounds);
      }, 200);
    }
  }
}

function changeZoom(changeBy: number) {
  if (googleMapRef.value?.map) {
    const currentZoom = googleMapRef.value.map.getZoom() ?? 0;
    const newZoom = Math.max(Math.min(currentZoom + changeBy, MAX_ZOOM), MIN_ZOOM);
    googleMapRef.value.map.setZoom(newZoom);
  }
}

function sortStoresByDistance(stores: Store[], locationResult: LocationResult) {
  return stores
    .map(store => ({
      distance: haversine(
        {
          latitude: store.content.location!.latitude!,
          longitude: store.content.location!.longitude!,
        },
        {
          latitude: locationResult.location.lat,
          longitude: locationResult.location.lng,
        }
      ),
      store: store,
    }))
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Focus map on selected store
 */
function handleStoreSelected(store: Store) {
  selectedStore.value = store;
  panToStore(store);
}

function panToStore(store: Store) {
  // Ensure the zoom level is reasonable (at least 10)
  const currentZoom = googleMapRef.value!.map!.getZoom() ?? 0;
  googleMapRef.value!.map!.setZoom(currentZoom < 10 ? 10 : currentZoom);

  if (store.content.location?.latitude && store.content.location?.longitude) {
    googleMapRef.value!.map!.panTo(
      new googleMapRef.value!.api!.LatLng(store.content.location.latitude, store.content.location.longitude)
    );
  }
}

/**
 * Animate layer fade in on mobile
 */
watch(
  () => sortedStores.value?.length ?? 0 > 0,
  () => {
    if ((sortedStores.value?.length ?? 0 > 0) && !breakpointState.isTablet && $mapContainer.value) {
      gsap.fromTo(
        $mapContainer.value,
        { height: $mapContainer.value.clientHeight },
        {
          height: 'auto',
          duration: 0.3,
          ease: 'ease-in-out',
          onComplete: function () {
            gsap.set(this.targets() as gsap.TweenTarget, { clearProps: 'all' });
          },
        }
      );
    }
  }
);

function handlePinClick(store: Store) {
  if (store.content.location?.latitude && store.content.location?.longitude) {
    if ((sortedStores.value ?? []).length === 0) {
      handleLocationChange({
        location: {
          lat: store.content.location.latitude,
          lng: store.content.location.longitude,
        },
        countryCode: undefined,
      });
    } else {
      handleStoreSelected(store);
    }
  }
}

/**
 * Handle the autocomplete dropdown visibility on mobile.
 * We add a black element similar to the search results for better visibility
 */
function handleFocus(isVisible: boolean, isResultSelect: boolean) {
  if (
    breakpointState.isTablet ||
    isResultSelect ||
    (sortedStores.value?.length ?? 0) > 0 ||
    mobileInputVisible.value === isVisible
  ) {
    // We don't want this to run on desktop or when a result is selected which triggers a different animation
    return;
  }

  const targetSize = breakpointState.isPhoneLarge ? 700 : 1250;
  mobileInputVisible.value = isVisible;
  if ($mapContainer.value) {
    gsap.fromTo(
      $mapContainer.value,
      { height: isVisible ? 500 : targetSize },
      {
        height: isVisible ? targetSize : 500,
        duration: 0.5,
        ease: 'ease-in-out',
      }
    );
  }
}
watch(
  () => breakpointState.isTablet,
  () => {
    // Reset the autocomplete dropdown visibility when switching to desktop
    if (breakpointState.isTablet && mobileInputVisible.value && $mapContainer.value) {
      gsap.set($mapContainer.value, { clearProps: 'all' });
    }
  }
);

/**
 * Cookie consent
 */

function requestConsentChange() {
  consentState.visible = 'preferences';
}

function handleModalAccept() {
  requestConsentChange();
}

function handleModalReject() {
  didAskForConsent.value = true;
}

/**
 * We need to define an algorithm for the marker clusterer otherwise the pins will
 * disappear when zooming out or paning very far, because of a low default viewport padding
 * This module needs to be imported like this to avoid the following error:
 * CommonJS modules can always be imported via the default export
 */
async function createGMapsAlgorithm() {
  const markerClustererPackage = await import('@googlemaps/markerclusterer');
  const { SuperClusterAlgorithm } = markerClustererPackage;
  algorithm.value = new SuperClusterAlgorithm({});
}

onMounted(() => {
  document.addEventListener(SebEvent.EmMapModalAccept, handleModalAccept);
  document.addEventListener(SebEvent.EmMapModalReject, handleModalReject);
  void createGMapsAlgorithm();
});

onUnmounted(() => {
  document.removeEventListener(SebEvent.EmMapModalAccept, handleModalAccept);
  document.removeEventListener(SebEvent.EmMapModalReject, handleModalReject);
});
</script>

<style src="./SharedEMMapBase.scss" lang="scss" scoped />
